import logo from '@/assets/icons/ui/logo.png';
import { homeData } from '@/data/home';
import { useIDEState } from '@/hooks/use-ide-state';
import React from 'react';

export interface PortfolioHomeProps {
    isMobile?: boolean;
}

export const PortfolioHome: React.FC<PortfolioHomeProps> = ({ isMobile = false }) => {
    const { navigateToFile } = useIDEState();
    const handlePortfolioNavigation = (event: React.MouseEvent<HTMLAnchorElement>, file: string) => {
        if (!isMobile && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
            event.preventDefault();
            navigateToFile('Portfolio', file);
        }
    };

    const handleGitHubClick = () => {
        window.open(homeData.openSource.links.github.url, '_blank');
    };

    const handleGistClick = () => {
        window.open(homeData.openSource.links.gist.url, '_blank');
    };

    const age = new Date().getFullYear() - homeData.birthDate.getFullYear();

    // Conditional styling based on mobile/desktop
    const containerClasses = isMobile
        ? 'min-h-full bg-vscode-bg text-vscode-text p-6'
        : 'min-h-full bg-vscode-bg text-vscode-text p-8 flex items-center justify-center';

    const maxWidthClasses = isMobile ? 'max-w-4xl mx-auto' : 'max-w-6xl mx-auto';

    const logoSizeClasses = isMobile ? 'w-20 h-20' : 'w-24 h-24';

    const logoMarginClasses = isMobile ? 'text-center mb-8' : 'text-center mb-6';

    const contentClasses = isMobile
        ? 'space-y-4 text-base leading-relaxed text-center'
        : 'space-y-2 text-lg leading-relaxed text-center';

    const openSourceMarginClasses = isMobile ? 'mt-10 mb-8 text-center' : 'mt-12 mb-6 text-center';

    const openSourceTitleClasses = isMobile
        ? 'text-2xl font-bold text-vscode-text mb-3'
        : 'text-2xl font-bold text-vscode-text mb-3';

    const openSourceSubtitleClasses = isMobile ? 'text-vscode-text-muted text-base' : 'text-vscode-text-muted text-lg';

    const buttonContainerClasses = isMobile
        ? 'flex flex-col sm:flex-row gap-4 justify-center items-center'
        : 'flex gap-4 justify-center items-center';

    return (
        <div className={containerClasses}>
            <div className={maxWidthClasses}>
                {/* Header with Logo */}
                <div className={logoMarginClasses}>
                    <div className='inline-block'>
                        <a href={homeData.social.linkedin} target='_blank'>
                            <img
                                src={logo}
                                alt='Faris Rehman Logo'
                                className={`${logoSizeClasses} rounded-full shadow-lg`}
                            />
                        </a>
                    </div>
                </div>

                {/* Main Content */}
                <div className={contentClasses}>
                    <p className='text-vscode-text'>
                        {homeData.introduction.greeting}{' '}
                        <span className='text-vscode-accent font-semibold'>{homeData.name}</span>, a {age} year old
                        {homeData.introduction.description}
                    </p>

                    <p className={isMobile ? 'text-vscode-text-muted' : 'text-vscode-text'}>
                        {homeData.introduction.background}
                    </p>

                    <p className={isMobile ? 'text-vscode-text-muted' : 'text-vscode-text'}>
                        I am currently working at{' '}
                        <a
                            href={homeData.introduction.currentWork.companyUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-400 hover:text-blue-300 transition-colors underline'
                        >
                            {homeData.introduction.currentWork.company}
                        </a>{' '}
                        in the{' '}
                        <a
                            href={homeData.introduction.currentWork.teamUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-400 hover:text-blue-300 transition-colors underline'
                        >
                            {homeData.introduction.currentWork.team}
                        </a>{' '}
                        ({homeData.introduction.currentWork.teamShort}) team as an{' '}
                        {homeData.introduction.currentWork.role}.
                    </p>
                </div>

                <div className='flex flex-wrap justify-center gap-3 mt-6'>
                    <a
                        href='/Portfolio/projects.html'
                        onClick={event => handlePortfolioNavigation(event, 'projects.html')}
                        className='px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold'
                    >
                        View Projects
                    </a>
                    <a
                        href='/Portfolio/contact.html'
                        onClick={event => handlePortfolioNavigation(event, 'contact.html')}
                        className='px-6 py-3 rounded-lg border border-vscode-accent text-vscode-accent hover:bg-vscode-bg-secondary'
                    >
                        Contact
                    </a>
                </div>

                {/* Open Source Section */}
                <div className={openSourceMarginClasses}>
                    <h2 className={openSourceTitleClasses}>{homeData.openSource.title}</h2>
                    <p className={openSourceSubtitleClasses}>{homeData.openSource.subtitle}</p>
                </div>

                {/* Action Buttons */}
                <div className={buttonContainerClasses}>
                    <button
                        onClick={handleGitHubClick}
                        className='w-32 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer'
                    >
                        <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z' />
                        </svg>
                        {homeData.openSource.links.github.label}
                    </button>

                    <button
                        onClick={handleGistClick}
                        className='w-32 px-6 py-3 bg-vscode-bg-secondary hover:bg-vscode-bg-tertiary text-blue-400 border border-blue-400 hover:border-blue-300 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer'
                    >
                        <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z' />
                        </svg>
                        {homeData.openSource.links.gist.label}
                    </button>
                </div>
            </div>
        </div>
    );
};
